import {
  child,
  get,
  onChildAdded,
  ref,
  set,
  type Unsubscribe,
} from "firebase/database";
import { ref as filesRef, getDownloadURL, uploadBytes } from "firebase/storage";
import { useEffect, useRef } from "react";
import { realtimeDB, storage } from "../firebaseConfig";
import { CanvasRibbonBrush } from "../scripts/CanvasRibbonBrush";
import { useAppSelector } from "../store/hooks";
import { userRoles } from "../store/slices/storeSlice";
import type { Action } from "./canvasTypes";

const useWhiteboard = (
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  setCurrentTool: (tool: string) => void,
  sessionId: number
) => {
  const { user } = useAppSelector((state) => state.store);
  const sessionListener = useRef<Unsubscribe | null>(null);
  const brush = useRef<CanvasRibbonBrush>(null);
  const sendActionToFirebase = async (action: Action) => {
    try {
      await set(
        ref(realtimeDB, `sessions/${sessionId}/${action.timestamp}`),
        action
      );
      return true;
    } catch (error) {
      console.error("Error setting data to Firebase:", error);
      throw error;
    }
  };

  const deleteSession = () => {
    set(ref(realtimeDB, `sessions/${sessionId}`), null);
  };

  (window as any).deleteSession = deleteSession;

  const uploadImgToFirebase = async (file: File) => {
    const storageRef = filesRef(
      storage,
      "live-sessions-files/" + file.name + "-" + Date.now()
    );

    const snapshot = await uploadBytes(storageRef, file);
    const fileUrl = await getDownloadURL(snapshot.ref);
    return fileUrl?.split("&token=")[0];
  };

  useEffect(() => {
    if (!canvasRef.current || brush.current) return;
    console.log("rerendering brush");
    brush.current = new CanvasRibbonBrush(
      canvasRef.current,
      {
        role: user.role === userRoles.TEACHER ? "tutor" : "student",
        screenHeight: canvasRef.current?.clientHeight,
        screenWidth: canvasRef.current?.clientWidth,
      },
      setCurrentTool,
      sendActionToFirebase,
      uploadImgToFirebase
    );

    return () => {
      brush.current?.destroy();
    };
  }, [canvasRef]);

  useEffect(() => {
    const fetchSessionData = () => {
      get(child(ref(realtimeDB), `sessions/${sessionId}`))
        .then((snapshot) => {
          if (snapshot.exists()) {
            const prevActions = snapshot.val() as { [id: string]: Action };
            const prevIds = Object.keys(prevActions);
            brush.current?.snapToCurrentState(Object.values(prevActions));

            if (user.role === userRoles.STUDENT) {
              if (sessionListener.current) return;

              sessionListener.current = onChildAdded(
                ref(realtimeDB, `sessions/${sessionId}`),
                (snapshot) => {
                  const data = snapshot.val();

                  if (prevIds.indexOf(data?.timestamp.toString()) === -1) {
                    brush.current?.doAction(data);
                  }
                }
              );
            }
            
            // Successfully found data, stop retrying
            if (retryInterval) {
              clearInterval(retryInterval);
            }
          } else {
            console.log("No data available, retrying in 5 seconds...");
          }
        })
        .catch((error) => {
          console.error("Error fetching session data, retrying in 5 seconds:", error);
        });
    };

    // Initial attempt
    fetchSessionData();

    // Set up retry interval
    const retryInterval = setInterval(fetchSessionData, 5000);

    // Cleanup function
    return () => {
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, []);

  const selectTool = () => {
    brush.current?.selectTool();
  };
  const penTool = () => {
    brush.current?.penTool();
  };
  const handTool = () => {
    brush.current?.handTool();
  };
  // const zoom = (type: "in" | "out") => {
  //   brush.current?.zoomTool(type);
  // };
  const textTool = () => {
    brush.current?.textTool();
  };
  const shapeTool = () => {
    brush.current?.shapeTool();
  };
  const insertShape = (shape: "rectangle" | "circle" | "triangle") => {
    brush.current?.insertShape(shape);
  };
  const eraserTool = () => {
    brush.current?.eraserTool();
  };
  const undo = () => {
    brush.current?.undo();
  };
  const redo = () => {
    brush.current?.redo();
  };
  const reset = () => {
    brush.current?.reset();
  };
  const takeScreenshot = () => {
    brush.current?.takeScreenshot();
  };
  const imageTool = () => {
    brush.current?.imageTool();
  };
  const colorPicker = (color: [number, number, number]) => {
    brush.current?.colorPicker(color);
  };
  const fontSizePicker = (size: number) => {
    brush.current?.fontSizePicker(size);
  };
  const resizeElement = (amount: number, direction: "x" | "y") => {
    brush.current?.resizeElement(amount, direction);
  };

  return {
    selectTool,
    penTool,
    handTool,
    // zoom,
    textTool,
    shapeTool,
    eraserTool,
    undo,
    redo,
    reset,
    takeScreenshot,
    imageTool,
    colorPicker,
    fontSizePicker,
    insertShape,
    resizeElement,
  };
};

export default useWhiteboard;
